import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/shared/constants/routes';
import type { AgentNavigation } from '@/viewmodel/shared/agentNavigation/types';

interface AgentLocationState {
  readonly from?: string;
}

function toReturnRoute(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const from = (state as AgentLocationState).from;
  return typeof from === 'string' ? from : null;
}

export function useAgentNavigation(): AgentNavigation {
  const navigate = useNavigate();
  const location = useLocation();

  const isAgentRoute =
    location.pathname === ROUTES.AGENT || location.pathname.startsWith(`${ROUTES.AGENT}/`);

  const enterAgent = useCallback(() => {
    if (isAgentRoute) return;
    navigate(ROUTES.AGENT, { state: { from: `${location.pathname}${location.search}` } });
  }, [isAgentRoute, navigate, location.pathname, location.search]);

  const exitAgent = useCallback(
    (fallback: string = ROUTES.DATA_STUDIO) => {
      navigate(toReturnRoute(location.state) ?? fallback, { replace: true });
    },
    [navigate, location.state],
  );

  return { isAgentRoute, enterAgent, exitAgent };
}

export function useAgentReturnRoute(): string | null {
  const location = useLocation();
  return toReturnRoute(location.state);
}
